import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Container,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Text,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  MenuGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useDisclosure,
  RadioGroup,
  Radio,
  Button,
  Checkbox,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiMoreVertical,
  FiEye,
  FiDownload,
  FiStar,
  FiRefreshCw,
  FiMessageSquare,
  FiChevronRight,
  FiChevronLeft,
  FiX,
  FiTrash2,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import config from '../../config/config';
import unifiedService from '../../services/api/unifiedService';
import notificationService from '../../services/api/notificationService';
import candidateService from '../../services/api/candidateService';
import reportService from '../../services/api/reportService';
import { getNominationStatusLabel, getNominationStatusColor } from '../../utils/evaluationStatusUtils';
import usePersistentState from '../../hooks/usePersistentState';

/**
 * CandidateReview component
 * Committee page for reviewing assigned candidates
 */
const CandidateReview = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { currentUser } = useAuth();

  // State
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  // Persist filters in sessionStorage
  const [searchTerm, setSearchTerm] = usePersistentState('candidateReview.search', '');
  const [statusFilter, setStatusFilter] = usePersistentState('candidateReview.status', 'all');
  // Default to first branch in config - will be updated when data is loaded
  const [branchFilter, setBranchFilter] = usePersistentState('candidateReview.branch', 'all');
  const [centerFilter, setCenterFilter] = usePersistentState('candidateReview.center', 'all');
  // Persist current page in sessionStorage
  const [currentPage, setCurrentPage] = usePersistentState('candidateReview.page', 1);
  // Persist per-page in localStorage
  const [itemsPerPage, setItemsPerPage] = usePersistentState('candidateReview.perPage', 10, 'local');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  // Delete states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [hasEval, setHasEval] = useState(false);
  const [evalCount, setEvalCount] = useState(0);
  const [deleteReason, setDeleteReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [confirmIdText, setConfirmIdText] = useState('');
  const [ackEvalDeletion, setAckEvalDeletion] = useState(false);


  // Modal controls
  const {
    isOpen: isSmsModalOpen,
    onOpen: onSmsModalOpen,
    onClose: onSmsModalClose
  } = useDisclosure();

  const {
    isOpen: isStatusModalOpen,
    onOpen: onStatusModalOpen,
    onClose: onStatusModalClose
  } = useDisclosure();

  // Pagination (server-side)
  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCandidates = candidates;

  // Determine if any filter is active (branch is locked and preserved)
  const isAnyFilterActive = (
    (searchTerm && searchTerm.trim() !== '') ||
    statusFilter !== 'all' ||
    centerFilter !== 'all'
  );

  // Clear filters (keep branchFilter as it is locked to user's branch)
  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCenterFilter('all');
    setCurrentPage(1);
  };

  // Handle delete candidate (open confirm modal with evaluation check)
  const handleDeleteCandidate = async (candidate) => {
    setSelectedCandidate(candidate);
    try {
      const check = await candidateService.hasEvaluations(candidate.id);
      setHasEval(!!check?.has_evaluations);
      setEvalCount(check?.count || 0);
    } catch (_e) {
      setHasEval(false);
      setEvalCount(0);
    }
    setDeleteConfirmOpen(true);
  };

  // Confirm delete
  const confirmDeleteCandidate = async () => {
    if (!selectedCandidate) return;
    try {
      const reasonToSend = deleteReason === 'other' ? otherReason.trim() : deleteReason;
      const response = await candidateService.deleteCandidate(selectedCandidate.id, {
        reason: reasonToSend || undefined,
        force: hasEval ? true : undefined,
      });

      if (response && response.success) {
        setCandidates(prev => prev.filter(c => c.id !== selectedCandidate.id));

        toast({
          title: 'تم الحذف بنجاح',
          description: 'تم حذف المرشح بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });

        closeDeleteModal();
      } else {
        throw new Error(response?.message || 'Failed to delete candidate');
      }
    } catch (error) {
      toast({
        title: 'خطأ في الحذف',
        description: error.message || 'حدث خطأ أثناء حذف المرشح',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Close and reset delete modal state
  const closeDeleteModal = () => {
    setDeleteConfirmOpen(false);
    setHasEval(false);
    setEvalCount(0);
    setSelectedCandidate(null);
    setDeleteReason('');
    setOtherReason('');
    setConfirmIdText('');
    setAckEvalDeletion(false);
  };

  // Track initialization (after first fetch + any auto-defaults)
  const initializedRef = useRef(false);

  // Fetch candidates data (server-side filters & pagination for committee)
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setLoading(true);

        // Build server-side filters. Backend enforces branch permissions, but we pass our selected filters too.
        const filters = {};
        if (searchTerm && searchTerm.trim() !== '') filters.search = searchTerm.trim();
        if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
        if (branchFilter && branchFilter !== 'all') filters.branch_id = branchFilter; // backend expects branch_id code
        if (centerFilter && centerFilter !== 'all') filters.center_id = centerFilter; // center code/id
        filters.skip = (currentPage - 1) * itemsPerPage;
        filters.limit = itemsPerPage;

        const [list, total] = await Promise.all([
          unifiedService.getCandidates(filters),
          candidateService.getCandidatesCount({
            search: filters.search,
            status: filters.status,
            branchId: branchFilter && branchFilter !== 'all' ? branchFilter : undefined,
            centerId: centerFilter && centerFilter !== 'all' ? centerFilter : undefined,
          })
        ]);

        const candidatesArray = Array.isArray(list) ? list : [];

        // Default branch filter from first item if not set (do not affect currentPage)
        if ((branchFilter === 'all' || !branchFilter) && candidatesArray.length > 0 && candidatesArray[0].branch) {
          setBranchFilter(candidatesArray[0].branch);
        }

        setCandidates(candidatesArray);
        setTotalCount(typeof total === 'number' ? total : (total?.total ?? candidatesArray.length));
        // Mark initialized after first data load + potential defaults
        if (!initializedRef.current) initializedRef.current = true;
      } catch (error) {
        console.error('Error fetching candidates:', error);
        toast({
          title: 'خطأ في جلب البيانات',
          description: 'حدث خطأ أثناء جلب بيانات المرشحين',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [toast, searchTerm, statusFilter, branchFilter, centerFilter, currentPage, itemsPerPage, setBranchFilter]);

  // Reset to first page on user-driven filter changes (skip until initialized)
  useEffect(() => {
    if (!initializedRef.current) return;
    setCurrentPage(1);
  }, [searchTerm, statusFilter, centerFilter, branchFilter, setCurrentPage]);

  // Handle view candidate
  const handleViewCandidate = (candidateId) => {
    navigate(`/committee/candidates/${candidateId}`);
  };

  // Open candidate details in a new window/tab
  const openCandidateInNewWindow = (candidateId) => {
    const url = `/committee/candidates/${candidateId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Handle evaluate candidate
  const handleEvaluateCandidate = (candidateId) => {
    navigate(`/committee/evaluate/${candidateId}`);
  };

  // Handle download nomination report (no attachments)
  const handleDownloadReport = async (candidate) => {
    try {
      const response = await reportService.downloadNominationReport(candidate.id, {
        includeEvaluation: false,
        includeAttachmentA: false,
        includeAttachmentB: false,
        format: 'pdf',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const candidateName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'candidate';
      link.download = `${candidateName.replace(/\s+/g, '_')}_report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'تم تنزيل التقرير بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      console.error('Error downloading report:', error);
      toast({
        title: 'حدث خطأ أثناء تنزيل التقرير',
        description: 'يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Open status change modal
  const openStatusChangeModal = (candidate) => {
    setSelectedCandidate(candidate);
    setNewStatus(candidate.status || '');
    onStatusModalOpen();
  };

  // Handle change status
  const handleChangeStatus = async (candidate, status, sendNotification = false) => {
    try {
      setLoading(true);
      // Use the proper updateCandidateStatus endpoint with notes parameter
      const response = await candidateService.updateCandidateStatus(
        candidate.id,
        status,
        `تم تحديث الحالة بواسطة ${currentUser?.role === 'admin' ? 'المسؤول' : 'عضو اللجنة'}`
      );

      if (response) {
        // Update local state
        const updatedCandidates = candidates.map((c) =>
          c.id === candidate.id ? { ...c, status: status } : c
        );

        setCandidates(updatedCandidates);

        toast({
          title: 'تم تحديث الحالة',
          description: `تم تغيير حالة المرشح إلى ${getNominationStatusLabel(status)}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });

        // Close modal if it was opened
        if (isStatusModalOpen) {
          onStatusModalClose();
        }
        // Optionally open SMS modal
        if (sendNotification) {
          openSmsModal(candidate);
        }
      } else {
        throw new Error(response?.message || 'Failed to update candidate status');
      }
    } catch (error) {
      toast({
        title: 'خطأ في تحديث الحالة',
        description: error.message || 'حدث خطأ أثناء تحديث حالة المرشح',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setLoading(false);
    }
  };

  // Submit status change from modal
  const submitStatusChange = () => {
    if (selectedCandidate && newStatus) {
      handleChangeStatus(selectedCandidate, newStatus, false);
    }
  };

  // Submit status change and open SMS modal
  const submitStatusChangeAndNotify = () => {
    if (selectedCandidate && newStatus) {
      handleChangeStatus(selectedCandidate, newStatus, true);
    }
  };

  // Open SMS modal
  const openSmsModal = (candidate) => {
    setSelectedCandidate(candidate);

    // Set default message based on candidate status
    const status = getNominationStatusLabel(candidate);
    const firstName = candidate.personalInfo?.firstName ||
                     candidate.personal_info?.first_name ||
                     candidate.first_name || '';
    const defaultMessage = `مرحباً ${firstName}، تم تحديث حالة طلبك إلى ${status}. للمزيد من المعلومات يرجى زيارة منصة مجالسنا.`;

    setSmsMessage(defaultMessage);
    onSmsModalOpen();
  };

  // Send SMS notification
  const sendSmsNotification = async () => {
    if (!selectedCandidate || !smsMessage) return;

    try {
      setIsSendingSms(true);

      const phoneNumber = selectedCandidate.personalInfo?.phone ||
                          selectedCandidate.personal_info?.phone ||
                          selectedCandidate.phone;

      if (!phoneNumber) {
        throw new Error('رقم الهاتف غير متوفر للمرشح');
      }

      // Use the correct function name from notificationService
      const response = await notificationService.sendCandidateSMS(selectedCandidate, smsMessage);

      // Backend returns a successful response with details.replyCode 0 when SMS is sent successfully
      // Check for success in various response formats
      console.log('SMS response:', response);
      if (response && (
        response.success ||
        response.status === 'success' ||
        response.status === 200 ||
        (response.details && response.details.replyCode === 0)
      )) {
        toast({
          title: 'تم إرسال الرسالة',
          description: 'تم إرسال الإشعار بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });

        onSmsModalClose();
      } else {
        throw new Error(response?.message || 'Failed to send SMS notification');
      }
    } catch (error) {
      toast({
        title: 'خطأ في إرسال الإشعار',
        description: error.message || 'حدث خطأ أثناء إرسال الإشعار',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      // Close modal if it was opened
      if (isSmsModalOpen) {
        onSmsModalClose();
      }
    } finally {
      setIsSendingSms(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA');
  };

  // Get status color scheme based on nomination status and evaluation status
  const getStatusColorScheme = (candidate) => {
    return getNominationStatusColor(candidate?.status);
  };

  // Get status label based on nomination status and evaluation status
  const getStatusLabel = (candidate) => {
    return getNominationStatusLabel(candidate?.status);
  };

  // Get branch name - always use branch_name directly from the data
  const getBranchName = (candidate) => {
    return candidate && candidate.branch_name ? candidate.branch_name : '';
  };

  // Get center name - always use center_name directly from the data
  const getCenterName = (candidate) => {
    return candidate && candidate.center_name ? candidate.center_name : '';
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8}>
        <Heading as="h1" size="lg" mb={2}>
          مراجعة المرشحين
        </Heading>
        <Text color="gray.600">
          قائمة المرشحين المخصصين لك للتقييم
        </Text>
      </Box>

      {/* Filters */}
      <Box mb={6} p={4} bg="white" borderRadius="lg" boxShadow="sm">
        <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
          {/* Search */}
          <InputGroup maxW={{ base: '100%', md: '300px' }}>
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="بحث بالاسم أو البريد الإلكتروني أو رقم الهوية"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          {/* Nomination Status Filter */}
          <Select
            placeholder="تصفية حسب الحالة"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
          >
            <option value="all">جميع الحالات</option>
            <option value="submitted">تحت التقييم</option>
            <option value="pending">في انتظار النتيجة</option>
            <option value="shortlisted">قبول أولي</option>
            <option value="accepted">مقبول</option>
            <option value="rejected">مرفوض</option>
            <option value="returned">عائد</option>
            <option value="draft">مسودة</option>
          </Select>

          {/* Branch Filter - Disabled for committee members as they can only see their own branch */}
          <Select
            placeholder="الفرع"
            value={branchFilter}
            isDisabled={true} /* Lock the branch filter */
            maxW={{ base: '100%', md: '200px' }}
          >
            {/* Only show the committee member's branch */}
            {branchFilter !== 'all' && (
              <option value={branchFilter}>
                {config.organization.branches.find(b => b.id === branchFilter)?.name || branchFilter}
              </option>
            )}
          </Select>

          {/* Center Filter */}
          <Select
            placeholder="تصفية حسب المركز"
            value={centerFilter}
            onChange={(e) => setCenterFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
            isDisabled={branchFilter === 'all'}
          >
            <option value="all">جميع المراكز</option>
            {config.organization.centers
              .filter((center) => {
                if (branchFilter === 'all') return true;
                const branch = config.organization.branches.find(b => b.id === center.branchId);
                return branch && branch.id === branchFilter;
              })
              .map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
          </Select>

          {/* Clear Filters Button */}
          <Button
            leftIcon={<FiX />}
            colorScheme={isAnyFilterActive ? 'red' : 'gray'}
            variant={isAnyFilterActive ? 'solid' : 'outline'}
            onClick={clearAllFilters}
            ml={{ base: 0, md: 'auto' }}
            title={isAnyFilterActive ? 'هناك عوامل تصفية مفعّلة - انقر لمسحها' : 'لا توجد عوامل تصفية مفعّلة'}
          >
            مسح التصفية
          </Button>
        </Stack>

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>المرشح</Th>
              <Th>الفرع</Th>
              <Th>المركز</Th>
              <Th>المنصب</Th>
              <Th>تاريخ التقديم</Th>
              <Th>حالة التقييم</Th>
              <Th>الإجراءات</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={7} textAlign="center" py={10}>
                  جاري تحميل البيانات...
                </Td>
              </Tr>
            ) : currentCandidates.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center" py={10}>
                  لا توجد بيانات متاحة
                </Td>
              </Tr>
            ) : (
              currentCandidates.map((candidate) => (
                <Tr key={candidate.id}>
                  <Td>
                    {candidate.first_name} {candidate.last_name}
                  </Td>
                  <Td>{getBranchName(candidate)}</Td>
                  <Td>{getCenterName(candidate)}</Td>
                  <Td>{candidate.job_title || 'غير محدد'}</Td>
                  <Td>{formatDate(candidate.submission_date)}</Td>
                  <Td>
                    <Badge
                      colorScheme={getStatusColorScheme(candidate)}
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      {getStatusLabel(candidate)}
                    </Badge>
                  </Td>
                  <Td>
                    <Flex gap={2} align="center">
                      <IconButton
                        icon={<FiEye />}
                        variant="ghost"
                        size="sm"
                        colorScheme="teal"
                        aria-label="عرض التفاصيل في نافذة جديدة"
                        onClick={() => openCandidateInNewWindow(candidate.id)}
                        title="عرض في نافذة جديدة"
                      />
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                          aria-label="المزيد من الخيارات"
                        />
                        <MenuList>
                          <MenuItem icon={<FiEye />} onClick={() => handleViewCandidate(candidate.id)}>
                            عرض التفاصيل
                          </MenuItem>
                          <MenuItem icon={<FiDownload />} onClick={() => handleDownloadReport(candidate)}>
                            تحميل التقرير
                          </MenuItem>
                          {!candidate.is_evaluated && (
                            <MenuItem icon={<FiStar />} onClick={() => handleEvaluateCandidate(candidate.id)}>
                              تقييم
                            </MenuItem>
                          )}
                          <MenuDivider />
                          <MenuGroup title="تغيير الحالة">
                            <MenuItem icon={<FiRefreshCw />} onClick={() => openStatusChangeModal(candidate)}>
                              تغيير الحالة
                            </MenuItem>
                            <MenuItem icon={<FiMessageSquare />} onClick={() => openSmsModal(candidate)}>
                              إرسال إشعار
                            </MenuItem>
                          </MenuGroup>
                          <MenuDivider />
                          {/* Delete option for committee users */}
                          {currentUser?.role === 'committee' && (
                            <MenuItem icon={<FiTrash2 />} onClick={() => handleDeleteCandidate(candidate)} color="red.500">
                              حذف
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>

        {/* Pagination and page size */}
        {!loading && totalCount > 0 && (
          <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor="gray.200">
            <Text color="gray.600">
              عرض {startIndex + 1} إلى {Math.min(endIndex, totalCount)} من أصل {totalCount} مرشح
            </Text>
            <Stack direction="row" spacing={3} align="center">
              <Select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                width="90px"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
              <IconButton
                icon={<FiChevronRight />}
                aria-label="الصفحة السابقة"
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              <IconButton
                icon={<FiChevronLeft />}
                aria-label="الصفحة التالية"
                isDisabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </Stack>
          </Flex>
        )}
      </Box>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmOpen} onClose={closeDeleteModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>تأكيد الحذف</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {hasEval && (
              <Box mb={3}>
                <Text color="red.600" fontWeight="bold">
                  تنبيه: لدى هذا المرشح {evalCount} تقييم(ات). سيتم حذف جميع التقييمات المرتبطة مع الحذف.
                </Text>
              </Box>
            )}
            <Text mb={2}>
              هل أنت متأكد من رغبتك في حذف بيانات المرشح{' '}
              {selectedCandidate && (
                `${selectedCandidate.first_name || ''} ${selectedCandidate.last_name || ''}`
              )}؟
            </Text>
            <Text color="red.500" mb={4}>
              هذا الإجراء لا يمكن التراجع عنه.
            </Text>

            <FormControl mb={3} isRequired>
              <FormLabel>سبب الحذف</FormLabel>
              <Select placeholder="اختر سبب الحذف" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)}>
                <option value="duplicate">تكرار السجل</option>
                <option value="user_request">طلب من المرشح</option>
                <option value="data_error">بيانات غير صحيحة</option>
                <option value="policy_violation">مخالفة السياسات</option>
                <option value="other">سبب آخر</option>
              </Select>
            </FormControl>

            {deleteReason === 'other' && (
              <FormControl mb={3} isRequired>
                <FormLabel>اذكر السبب</FormLabel>
                <Textarea value={otherReason} onChange={(e) => setOtherReason(e.target.value)} rows={3} placeholder="يرجى توضيح سبب الحذف" />
              </FormControl>
            )}

            {hasEval && (
              <FormControl display="flex" alignItems="center" mb={3}>
                <Checkbox isChecked={ackEvalDeletion} onChange={(e) => setAckEvalDeletion(e.target.checked)} colorScheme="red">
                  أُقر بأن جميع التقييمات المرتبطة سيتم حذفها مع بيانات المرشح
                </Checkbox>
              </FormControl>
            )}

            <FormControl mb={3} isRequired>
              <FormLabel>اكتب رقم المرشح للتأكيد</FormLabel>
              <Input
                placeholder={selectedCandidate ? String(selectedCandidate.id) : ''}
                value={confirmIdText}
                onChange={(e) => setConfirmIdText(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Stack direction="row-reverse" spacing={3} w="100%" justify="flex-start">
              <Button
                colorScheme="red"
                onClick={confirmDeleteCandidate}
                isDisabled={
                  !selectedCandidate ||
                  String(confirmIdText).trim() !== String(selectedCandidate?.id) ||
                  (hasEval && !ackEvalDeletion) ||
                  !deleteReason ||
                  (deleteReason === 'other' && !otherReason.trim())
                }
              >
                حذف نهائي
              </Button>
              <Button variant="outline" onClick={closeDeleteModal}>إلغاء</Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Status Change Modal */}
      <Modal isOpen={isStatusModalOpen} onClose={onStatusModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>تغيير حالة المرشح</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedCandidate && (
              <>
                <Text mb={4}>
                  المرشح: {selectedCandidate.first_name} {selectedCandidate.last_name}
                </Text>
                <FormControl mb={4}>
                  <FormLabel>اختر الحالة الجديدة</FormLabel>
                  <RadioGroup onChange={setNewStatus} value={newStatus}>
                    <Stack direction="column">
                      <Radio value="submitted">تحت التقييم</Radio>
                      <Radio value="pending">في انتظار النتيجة</Radio>
                      <Radio value="shortlisted">قبول أولي</Radio>
                      <Radio value="accepted">مقبول</Radio>
                      <Radio value="rejected">مرفوض</Radio>
                      <Radio value="returned">عائد</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Stack direction="row-reverse" spacing={3} w="100%" justify="flex-start">
              <Button
                colorScheme="teal"
                onClick={submitStatusChangeAndNotify}
                isLoading={loading}
                isDisabled={!newStatus}
              >
                تحديث الحالة وإرسال إشعار
              </Button>
              <Button
                colorScheme="blue"
                onClick={submitStatusChange}
                isLoading={loading}
                isDisabled={!newStatus}
              >
                تحديث الحالة
              </Button>
              <Button variant="outline" onClick={onStatusModalClose}>إلغاء</Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* SMS Notification Modal */}
      <Modal isOpen={isSmsModalOpen} onClose={onSmsModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>إرسال إشعار للمرشح</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedCandidate && (
              <>
                <Text mb={4}>
                  المرشح: {selectedCandidate.first_name} {selectedCandidate.last_name}
                </Text>
                <FormControl mb={4}>
                  <FormLabel>نص الرسالة</FormLabel>
                  <Textarea
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    placeholder="أدخل نص الرسالة هنا"
                    rows={5}
                  />
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Stack direction="row-reverse" spacing={3} w="100%" justify="flex-start">
              <Button
                colorScheme="blue"
                onClick={sendSmsNotification}
                isLoading={isSendingSms}
                isDisabled={!smsMessage}
              >
                إرسال
              </Button>
              <Button variant="outline" onClick={onSmsModalClose}>إلغاء</Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default CandidateReview;
